import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowUpDown,
  Bath,
  Brush,
  ChefHat,
  Construction,
  FolderPlus,
  ListFilter,
  Package,
  Pencil,
  Plus,
  Search,
  Square,
  Trash2,
  WashingMachine,
  Zap,
} from "lucide-react";
import { api, ApiError, type ProjectListRow } from "../api";
import {
  Card,
  PageHeader,
  PageHeaderTab,
  StatusBadge,
  cx,
} from "../ui";
import { useAuth } from "../auth";

type FilterTab =
  | "all"
  | "drafts"
  | "review"
  | "approved"
  | "completed"
  | "archived";

// Statuses an owner can edit — must match server-side EDITABLE_CODES
// in src/routes/projects.ts. Drives the inline Edit button on the
// project list.
const OWNER_EDITABLE_STATUSES = new Set([
  "draft",
  "pending_owner_revisions",
  "resubmission_required",
]);

// Drafts idle for this many days are treated as soft-archived: they
// drop out of the default tabs and surface only under \"Archived\".
// Editing wakes them (PATCH bumps updated_at).
const ARCHIVE_DAYS = 90;

const isArchivedDraft = (r: ProjectListRow) => {
  if (r.statusCode !== "draft") return false;
  const idleDays = Math.floor(
    (Date.now() - new Date(r.updatedAt).getTime()) / 86_400_000,
  );
  return idleDays >= ARCHIVE_DAYS;
};

const TABS: Array<{
  key: FilterTab;
  label: string;
  matches: (r: ProjectListRow) => boolean;
}> = [
  {
    key: "all",
    label: "All",
    matches: (r) => !isArchivedDraft(r),
  },
  {
    key: "drafts",
    label: "Drafts",
    matches: (r) => r.statusCode === "draft" && !isArchivedDraft(r),
  },
  {
    key: "review",
    label: "In Review",
    matches: (r) =>
      r.statusCode.startsWith("under_") ||
      r.statusCode === "submitted" ||
      r.statusCode === "pending_owner_revisions",
  },
  {
    key: "approved",
    label: "Approved",
    matches: (r) =>
      r.statusCode === "approved" ||
      r.statusCode === "conditionally_approved" ||
      r.statusCode === "scheduled" ||
      r.statusCode === "in_progress",
  },
  {
    key: "completed",
    label: "Completed",
    matches: (r) => r.statusCode === "completed" || r.statusCode === "closed",
  },
  {
    key: "archived",
    label: "Archived",
    matches: (r) => isArchivedDraft(r),
  },
];

export function ProjectsPage() {
  const { state } = useAuth();
  const [rows, setRows] = useState<ProjectListRow[] | null>(null);
  const [tab, setTab] = useState<FilterTab>("all");
  const [search, setSearch] = useState("");
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get("focus") === "search") {
      searchInputRef.current?.focus();
      const next = new URLSearchParams(searchParams);
      next.delete("focus");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const reload = () =>
    void api
      .get<{ data: ProjectListRow[] }>("/api/projects")
      .then((r) => setRows(r.data))
      .catch(() => setRows([]));

  useEffect(() => {
    reload();
  }, []);

  const onDelete = async (row: ProjectListRow) => {
    const confirmed = window.confirm(
      `Delete the draft "${row.title}"? This can't be undone.`,
    );
    if (!confirmed) return;
    setDeletingId(row.id);
    try {
      await api.del(`/api/projects/${row.id}`);
      reload();
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? typeof err.body === "object" && err.body && "error" in err.body
            ? String((err.body as { error: string }).error)
            : `HTTP ${err.status}`
          : String(err);
      window.alert(`Couldn't delete: ${msg}`);
    } finally {
      setDeletingId(null);
    }
  };

  const filtered = useMemo(() => {
    if (!rows) return [];
    const matcher = TABS.find((t) => t.key === tab)!.matches;
    const q = search.trim().toLowerCase();
    return rows.filter(
      (r) => matcher(r) && (!q || r.title.toLowerCase().includes(q)),
    );
  }, [rows, tab, search]);

  const eyebrow =
    state.status === "authenticated"
      ? `${state.me.user.firstName} ${state.me.user.lastName}`
      : undefined;

  return (
    <div>
      <PageHeader
        eyebrow={eyebrow}
        title="Projects"
        subtitle="Every project you can see, scoped by your role at the building."
        actions={
          <Link to="/projects/new" className="btn-on-dark no-underline hover:no-underline">
            <Plus size={15} />
            New project
          </Link>
        }
        tabs={TABS.map((t) => (
          <PageHeaderTab
            key={t.key}
            active={tab === t.key}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </PageHeaderTab>
        ))}
      />

      {/* Toolbar */}
      <div className="flex items-center gap-2.5 mb-5 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-[340px]">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400"
          />
          <input
            ref={searchInputRef}
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search projects…"
            className="input pl-9 h-[37px] py-0"
          />
        </div>
        <button className="btn-secondary btn-sm h-[37px]">
          <ListFilter size={13} /> Filter
        </button>
        <button className="btn-secondary btn-sm h-[37px]">
          <ArrowUpDown size={13} /> Sort
        </button>
      </div>

      {rows === null ? (
        <Card>
          <p className="text-ink-500">Loading…</p>
        </Card>
      ) : filtered.length === 0 ? (
        <EmptyState />
      ) : (
        <Card className="!p-0 overflow-hidden">
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th className="hidden sm:table-cell">Submitted</th>
                  <th>Status</th>
                  <th className="w-16 text-right"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const updatedDate = new Date(r.updatedAt);
                  const createdDate = new Date(r.createdAt);
                  const wasModified =
                    updatedDate.getTime() - createdDate.getTime() > 60_000;
                  const idleDays = Math.floor(
                    (Date.now() - updatedDate.getTime()) / 86_400_000,
                  );
                  const archivedDraft = isArchivedDraft(r);
                  const ageLabel = (() => {
                    if (idleDays < 30) return `${idleDays}d idle`;
                    if (idleDays < 60) return "1 month stale";
                    if (idleDays < 90) return `${Math.floor(idleDays / 30)} months stale`;
                    return `${Math.floor(idleDays / 30)} months stale`;
                  })();
                  const aging = archivedDraft
                    ? { tone: "rejected", label: "Archived" }
                    : r.statusCode === "draft" && idleDays >= 14
                      ? idleDays >= 60
                        ? { tone: "rejected", label: ageLabel }
                        : { tone: "warn", label: ageLabel }
                      : null;
                  return (
                  <tr key={r.id}>
                    <td>
                      <Link
                        to={`/projects/${r.id}`}
                        className="text-ink-800 font-medium hover:text-brand-700 no-underline hover:no-underline inline-flex items-center gap-2"
                      >
                        {r.isEmergency && (
                          <AlertTriangle
                            size={14}
                            className="text-status-rejected-fg"
                            aria-label="Emergency"
                          />
                        )}
                        {r.title}
                      </Link>
                      <div className="text-xs text-ink-500 mt-0.5 flex items-center gap-2 flex-wrap">
                        <span>
                          Created {createdDate.toLocaleDateString()}
                        </span>
                        {wasModified && (
                          <span
                            title={updatedDate.toLocaleString()}
                            className="text-ink-400"
                          >
                            · Modified{" "}
                            {updatedDate.toLocaleDateString()}
                          </span>
                        )}
                        {aging && (
                          <span
                            className={cx(
                              "inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold",
                              aging.tone === "warn"
                                ? "bg-status-review-bg text-status-review-fg"
                                : "bg-status-rejected-bg text-status-rejected-fg",
                            )}
                          >
                            {aging.label}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="hidden sm:table-cell text-ink-500 text-[13px]">
                      {r.submittedAt
                        ? new Date(r.submittedAt).toLocaleDateString()
                        : "—"}
                    </td>
                    <td>
                      <StatusBadge status={r.statusCode} />
                    </td>
                    <td className="text-right">
                      <div className="inline-flex items-center gap-1.5 justify-end">
                        {OWNER_EDITABLE_STATUSES.has(r.statusCode) && (
                          <Link
                            to={`/projects/${r.id}?edit=1`}
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-[12px] text-brand-700 hover:text-brand-800 bg-brand-50 hover:bg-brand-100 border border-brand-100 rounded-md no-underline hover:no-underline transition-colors"
                            aria-label={`Edit ${r.title}`}
                          >
                            <Pencil size={12} />
                            Edit
                          </Link>
                        )}
                        {r.statusCode === "draft" && (
                          <button
                            type="button"
                            onClick={() => void onDelete(r)}
                            disabled={deletingId === r.id}
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-[12px] text-status-rejected-fg hover:text-white bg-status-rejected-bg hover:bg-status-rejected-fg border border-status-rejected-fg/30 rounded-md transition-colors disabled:opacity-50"
                            aria-label={`Delete draft ${r.title}`}
                          >
                            <Trash2 size={12} />
                            {deletingId === r.id ? "Deleting…" : "Delete"}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Empty state with quick-start cards
// ---------------------------------------------------------------------------
function EmptyState() {
  return (
    <Card>
      <div className="flex flex-col items-center text-center py-10 px-4">
        <div className="w-[72px] h-[72px] rounded-[18px] bg-brand-50 border border-brand-100 flex items-center justify-center mb-5">
          <FolderPlus size={32} className="text-brand-600" />
        </div>
        <h2 className="text-ink-800">No projects yet</h2>
        <p className="text-[13.5px] text-ink-600 max-w-md mt-1.5 mb-7 leading-relaxed">
          Start a renovation or improvement project at your unit. We'll walk
          you through what's needed for approval — step by step.
        </p>
        <Link
          to="/projects/new"
          className="btn-primary no-underline hover:no-underline"
        >
          <Plus size={15} />
          Start your first project
        </Link>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5 max-w-2xl w-full mt-9">
          <QuickStart
            to="/projects/new?type=bathroom"
            icon={<Bath />}
            title="Bathroom renovation"
            sub="Fixtures, tile, plumbing"
          />
          <QuickStart
            to="/projects/new?type=flooring_hard"
            icon={<Square />}
            title="Hard flooring"
            sub="Wood, stone, ceramic tile"
          />
          <QuickStart
            to="/projects/new?type=kitchen"
            icon={<ChefHat />}
            title="Kitchen remodel"
            sub="Cabinets, counters, appliances"
          />
        </div>
      </div>
    </Card>
  );
}

function QuickStart({
  to,
  icon,
  title,
  sub,
}: {
  to: string;
  icon: React.ReactNode;
  title: string;
  sub: string;
}) {
  return (
    <Link
      to={to}
      className={cx(
        "group block bg-white border border-sand-100 rounded-lg p-4 text-left",
        "hover:border-brand-400 hover:-translate-y-0.5 hover:shadow-soft transition-all",
        "no-underline hover:no-underline",
      )}
    >
      <div className="text-brand-700 mb-2.5 group-hover:text-brand-800 transition-colors">
        <span className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-brand-50 border border-brand-100">
          {icon}
        </span>
      </div>
      <div className="text-[12.5px] font-semibold text-ink-800 mb-0.5">
        {title}
      </div>
      <div className="text-[11.5px] text-ink-400 leading-snug">{sub}</div>
    </Link>
  );
}

// Keep imports referenced for icon-by-type lookups in NewProjectPage
void Brush; void Construction; void Package; void WashingMachine; void Zap;
